import React, { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  size,
  autoUpdate,
} from '@floating-ui/react';
import {
  EDUCATION_LEVEL_DEFINITIONS,
  getLevelGroup,
  type EducationSystem,
  type EducationLevel,
} from '@tutor/types';

interface LevelMultiSelectProps {
  value: EducationLevel[];
  onChange: (levels: EducationLevel[]) => void;
  placeholder?: string;
  disabled?: boolean;
  systemId?: EducationSystem | string;
  optionLabels?: Partial<Record<string, string>>;
}

const LevelMultiSelect: React.FC<LevelMultiSelectProps> = ({
  value,
  onChange,
  placeholder = 'Select education levels',
  disabled = false,
  systemId,
  optionLabels,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: '224px',
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const selectedGroup = value.length > 0 ? getLevelGroup(value[0]) : null;
  const levelDefinitions = systemId
    ? EDUCATION_LEVEL_DEFINITIONS.filter((def) => def.systemId === systemId)
    : EDUCATION_LEVEL_DEFINITIONS;

  const isLevelDisabled = (level: EducationLevel): boolean => {
    if (value.length === 0) return false;
    return getLevelGroup(level) !== selectedGroup;
  };

  const handleSelectLevel = (level: EducationLevel) => {
    if (isLevelDisabled(level)) return;
    if (value.includes(level)) {
      onChange(value.filter((v) => v !== level));
    } else {
      onChange([...value, level]);
    }
  };

  const handleRemoveTag = (level: EducationLevel, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== level));
  };

  return (
    <div className="w-full">
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-primary/20 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1 flex-1 min-h-[24px]">
            {value.length > 0 ? (
              value.map((level) => {
                const label =
                  optionLabels?.[level] ??
                  EDUCATION_LEVEL_DEFINITIONS.find((d) => d.value === level)?.label;
                return (
                  <span
                    key={level}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-accent text-white text-sm rounded"
                  >
                    {label}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveTag(level, e)}
                      className="hover:opacity-75"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })
            ) : (
              <span className="text-gray-500 text-sm">{placeholder}</span>
            )}
          </div>
          <ChevronDown
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="bg-white border border-gray-200 rounded-lg shadow-lg z-[1040] overflow-hidden flex flex-col"
        >
          <div className="overflow-y-auto">
            {levelDefinitions.map((def) => {
              const isSelected = value.includes(def.value);
              const isDisabledOption = isLevelDisabled(def.value);
              const optionLabel = optionLabels?.[def.value] ?? def.label;
              return (
                <button
                  key={def.value}
                  type="button"
                  onClick={() => handleSelectLevel(def.value)}
                  disabled={isDisabledOption}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                    isDisabledOption ? 'opacity-40 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-100'
                  } ${isSelected ? 'bg-accent/5' : ''}`}
                >
                  <div
                    className={`w-4 h-4 border border-gray-300 rounded flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-accent border-accent' : ''
                    }`}
                  >
                    {isSelected && <span className="text-white text-xs">✓</span>}
                  </div>
                  <span>{optionLabel}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LevelMultiSelect;
